import React, { useMemo } from 'react';
import { FeatureAppLoader } from '@feature-hub/react';
import styled from 'styled-components';
import { getVideoPlayerConfig } from '../../../helper/getVideoConfig';
import { UeElement } from '@oneaudi/falcon-tools';
import { SYS_BREAKPOINT_MD } from '@oneaudi/unified-web-common';
import { useWindowSize } from '../../../helper/useWindowSize';

type Asset = {
  videoUrlLargeViewport: {
    assetValue: string;
  };
  videoUrlSmallViewport: {
    assetValue: string;
  };
  ueElementProperty: string;
  alternateDescription: string;
  videoHasAudio: boolean;
  preserveVideoWidth?: boolean;
  loop: boolean;

  closedCaption:
    | {
        path: string;
      }
    | undefined;
  autoPlay: boolean;
  alternateDescriptionPortrait: string;
  closedCaptionPortrait:
    | {
        path: string;
      }
    | undefined;
  audioDescription:
    | {
        path: string;
      }
    | undefined;
  audioDescriptionPortrait:
    | {
        path: string;
      }
    | undefined;
};

interface VideoProps {
  index: number;
  isCurrent: number;
  asset: Asset;
  ueElementProperty: string;
  height: number | undefined;
  testId?: string;
  galleryInstanceId: string;
  onPlaybackStarted?: () => void;
  onPlaybackPaused?: () => void;
  onPlaybackEnded?: () => void;
}

type Options = {
  root: null;
  rootElement: null;
  threshold: [0.01];
};

export const VideoPLayer: React.FC<VideoProps> = ({
  index,
  isCurrent,
  asset,
  height,
  testId = 'video-player-wrapper',
  galleryInstanceId,
  onPlaybackStarted,
  onPlaybackPaused,
  onPlaybackEnded,
}) => {
  const {
    videoUrlLargeViewport,
    videoUrlSmallViewport,
    ueElementProperty,
    alternateDescription: alt,
    alternateDescriptionPortrait: altPortrait,
    autoPlay,
    loop,
    closedCaption: caption,
    closedCaptionPortrait: captionPortrait,
    audioDescription,
    audioDescriptionPortrait,
    videoHasAudio: muted,
    preserveVideoWidth = false,
  } = asset;

  const videoPlayerWrapperRef = React.useRef<HTMLDivElement>(null);
  const serverSrcFile = process.env.IS_LEGACY ? '/fh/app.node.js' : '/fh/app-alpha.node.js';
  const clientSrcFile = process.env.IS_LEGACY ? '/fh/app.js' : '/fh/app-alpha.js';
  const videoPlayerFeatureAppBaseUrl = `https://fa-video-player.cdn.prod.collab.apps.one.audi/v5.6.1`;
  const videoPlayerFeatureAppSrc = `${videoPlayerFeatureAppBaseUrl}${clientSrcFile}`;
  const videoPlayerFeatureAppServerSrc = `${videoPlayerFeatureAppBaseUrl}${serverSrcFile}`;
  const [visible, setVisible] = React.useState(false);
  const { width } = useWindowSize();
  const isMobile = (width as number) < Number(SYS_BREAKPOINT_MD.replace('px', ''));

  const featureAppId = `__FEATURE_APP_NAME__${galleryInstanceId}-${index}`;

  const bindVideoListeners = React.useCallback(
    (videoElement: HTMLVideoElement) => {
      const handlePlay = () => {
        onPlaybackStarted?.();
      };

      const handleEnded = () => {
        onPlaybackEnded?.();
      };

      const handlePause = () => {
        onPlaybackPaused?.();
      };

      videoElement.addEventListener('play', handlePlay);
      videoElement.addEventListener('pause', handlePause);
      videoElement.addEventListener('ended', handleEnded);

      return () => {
        videoElement.removeEventListener('play', handlePlay);
        videoElement.removeEventListener('pause', handlePause);
        videoElement.removeEventListener('ended', handleEnded);
      };
    },
    [onPlaybackEnded, onPlaybackPaused, onPlaybackStarted],
  );

  const videoPlayerConfig = useMemo(() => {
    return getVideoPlayerConfig({
      alt,
      altPortrait,
      audioDescription: audioDescription?.path,
      audioDescriptionPortrait: audioDescriptionPortrait?.path,
      autoPlay,
      caption: caption?.path,
      captionPortrait: captionPortrait?.path,
      loop,
      muted,
      videoUrlLargeScreen: videoUrlLargeViewport.assetValue,
      videoUrlSmallScreen: videoUrlSmallViewport.assetValue,
      isMobile,
      preserveVideoWidth,
    });
  }, [
    alt,
    altPortrait,
    audioDescription?.path,
    audioDescriptionPortrait?.path,
    autoPlay,
    caption?.path,
    captionPortrait?.path,
    isMobile,
    loop,
    muted,
    preserveVideoWidth,
    videoUrlLargeViewport.assetValue,
    videoUrlSmallViewport.assetValue,
  ]);

  function isOnScreen(options: Options) {
    const observer = new IntersectionObserver(([entry]) => {
      setVisible(entry.isIntersecting);
    }, options);

    if (videoPlayerWrapperRef.current) {
      observer.observe(videoPlayerWrapperRef.current);
    }
    // if (!visible) {
    //   setRenderVideo(false);
    // }

    return () => {
      if (videoPlayerWrapperRef.current) {
        observer.unobserve(videoPlayerWrapperRef.current);
      }
    };
  }

  React.useEffect(() => {
    if (!videoPlayerWrapperRef.current) return;
    isOnScreen({
      root: null,
      rootElement: null,
      threshold: [0.01],
    });

    return undefined;
  }, []);

  React.useEffect(() => {
    if (!videoPlayerWrapperRef.current || index !== isCurrent) return;

    let removeVideoListeners: (() => void) | undefined;

    const tryBindVideoListeners = () => {
      const videoElement = videoPlayerWrapperRef.current?.querySelector('video');
      if (!videoElement) return false;
      removeVideoListeners = bindVideoListeners(videoElement);
      return true;
    };

    if (tryBindVideoListeners()) {
      return () => {
        removeVideoListeners?.();
      };
    }

    const observer = new MutationObserver(() => {
      if (tryBindVideoListeners()) {
        observer.disconnect();
      }
    });

    observer.observe(videoPlayerWrapperRef.current, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      removeVideoListeners?.();
    };
  }, [bindVideoListeners, index, isCurrent, videoPlayerConfig]);

  return (
    <UeElement type="media" property={ueElementProperty} label="Video">
      <VideoPlayerWrapper ref={videoPlayerWrapperRef} height={height} data-testid={testId}>
        {index === isCurrent && visible && typeof window !== 'undefined' ? (
          <FeatureAppLoader
            key={featureAppId}
            config={videoPlayerConfig}
            featureAppId={featureAppId}
            serverSrc={videoPlayerFeatureAppServerSrc}
            src={videoPlayerFeatureAppSrc}
          />
        ) : null}
      </VideoPlayerWrapper>
    </UeElement>
  );
};

const VideoPlayerWrapper = styled.div<{ height: number | undefined }>`
  position: unset;
  height: ${(props) => props.height}px;
  padding: 0;
  > div {
    height: ${(props) => props.height}px;
    padding-bottom: 0;
    video {
      height: ${(props) => props.height}px;
      position: unset;
    }
  }
`;
